import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

type LegalConsentShieldProps = {
  shopDomain: string;
};

export function LegalConsentShield({ shopDomain }: LegalConsentShieldProps) {
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleLock = () => setLocked(true);
    window.addEventListener('reach:legal-consent-required', handleLock);
    return () => window.removeEventListener('reach:legal-consent-required', handleLock);
  }, []);

  if (!locked) return null;

  const handleAgree = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/merchant/legal/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
           storeDomain: shopDomain
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(data?.error || 'Failed to record consent.');
      }

      // Success! Reload the page to clear the lock and re-fetch data
      window.location.reload();
    } catch (err: any) {
      setError(err?.message || 'An error occurred. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 99999,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{
        maxWidth: '520px',
        background: '#fff',
        borderRadius: '32px',
        padding: '40px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
        border: '1px solid #f1f5f9',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          background: '#f8fafc',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          fontSize: '28px'
        }}>
          🛡️
        </div>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 800,
          color: '#0f172a',
          marginBottom: '12px',
          letterSpacing: '-0.02em'
        }}>
          Legal Compliance Required
        </h2>
        <p style={{
          fontSize: '15px',
          color: '#64748b',
          lineHeight: 1.6,
          marginBottom: '32px'
        }}>
          To protect your store and your customers, we require all merchants to review and agree to our updated legal framework before accessing the Reach Dashboard.
        </p>

        <div style={{
          background: '#f8fafc',
          borderRadius: '20px',
          padding: '20px',
          marginBottom: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          textAlign: 'left'
        }}>
           <a href="/legal/terms" target="_blank" style={linkStyle}>Terms of Service {"->"}</a>
           <a href="/legal/privacy" target="_blank" style={linkStyle}>Privacy Policy {"->"}</a>
           <a href="/legal/dpa" target="_blank" style={linkStyle}>Data Processing Addendum {"->"}</a>
        </div>

        {error && <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '16px', fontWeight: 600 }}>{error}</p>}

        <button 
          onClick={handleAgree}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '16px',
            background: '#0f172a',
            color: '#fff',
            borderRadius: '16px',
            fontSize: '16px',
            fontWeight: 700,
            border: 'none',
            cursor: submitting ? 'not-allowed' : 'pointer',
            transition: 'transform 0.2s ease',
            opacity: submitting ? 0.7 : 1
          }}
        >
          {submitting ? 'Recording Consent...' : 'I Agree & Continue'}
        </button>

        <div style={{ marginTop: '24px' }}>
          <a 
            href={`https://${shopDomain || 'admin'}/admin/apps`}
            style={{
              fontSize: '13px',
              color: '#94a3b8',
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            Don't agree? Cancel & Uninstall App
          </a>
        </div>
        
        <p style={{ marginTop: '20px', fontSize: '11px', color: '#cbd5e1' }}>
          Agreement recorded via IP for audit purposes.
        </p>
      </div>
    </div>
  );
}

const linkStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#475569',
  textDecoration: 'none',
  display: 'block'
};
